import { z } from 'zod';

/**
 * Property (docs/specs/property.md, D60): compliance, issues → work orders → POs, suppliers,
 * leases (restricted), meters. Dates are YYYY-MM-DD; instants ISO; money is a decimal string
 * ("1234.50") so nothing is lost to floating point on the way through.
 */

const IsoDate = z.iso.date();
const Instant = z.iso.datetime();
const Money = z.string().regex(/^-?\d+(\.\d{1,2})?$/, 'Use pounds and pence, e.g. 125.50');
const MoneyIn = z
  .union([z.number(), z.string()])
  .transform((v) => (typeof v === 'number' ? v.toFixed(2) : v.trim()))
  .pipe(Money);
export const YearMonthPeriodSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use YYYY-MM');

// Vocabularies ---------------------------------------------------------------------

export const PropertyCategorySchema = z.enum([
  'repair',
  'maintenance',
  'health_safety',
  'security',
  'compliance',
  'other',
]);
export type PropertyCategory = z.infer<typeof PropertyCategorySchema>;
export const PROPERTY_CATEGORY_LABELS: Record<PropertyCategory, string> = {
  repair: 'Repair',
  maintenance: 'Maintenance',
  health_safety: 'Health & safety',
  security: 'Security',
  compliance: 'Compliance',
  other: 'Other',
};

export const UrgencySchema = z.enum(['low', 'normal', 'urgent']);
export type Urgency = z.infer<typeof UrgencySchema>;
export const URGENCY_LABELS: Record<Urgency, string> = {
  low: 'Low',
  normal: 'Normal',
  urgent: 'Urgent',
};

export const IssueStatusSchema = z.enum(['reported', 'triaged', 'converted', 'rejected']);
export type IssueStatus = z.infer<typeof IssueStatusSchema>;
export const ISSUE_STATUS_LABELS: Record<IssueStatus, string> = {
  reported: 'Reported',
  triaged: 'Triaged',
  converted: 'Job raised',
  rejected: 'Rejected',
};

export const WorkOrderStatusSchema = z.enum([
  'raised',
  'approved',
  'po_sent',
  'in_progress',
  'done_reported',
  'closed',
  'cancelled',
]);
export type WorkOrderStatus = z.infer<typeof WorkOrderStatusSchema>;
export const WORK_ORDER_STATUS_LABELS: Record<WorkOrderStatus, string> = {
  raised: 'Awaiting approval',
  approved: 'Approved',
  po_sent: 'PO sent',
  in_progress: 'In progress',
  done_reported: 'Done, awaiting invoice',
  closed: 'Closed',
  cancelled: 'Cancelled',
};
/** Statuses where work is still open (on the board). */
export const OPEN_WORK_ORDER_STATUSES: readonly WorkOrderStatus[] = [
  'raised',
  'approved',
  'po_sent',
  'in_progress',
  'done_reported',
];

export const WorkOrderActionSchema = z.enum([
  'edit',
  'approve',
  'resend_po',
  'mark_po_sent',
  'start',
  'confirm_done',
  'record_invoice',
  'close',
  'cancel',
]);
export type WorkOrderAction = z.infer<typeof WorkOrderActionSchema>;

export const ComplianceStatusSchema = z.enum([
  'valid',
  'due_soon',
  'expired',
  'missing',
  'not_applicable',
]);
export type ComplianceStatus = z.infer<typeof ComplianceStatusSchema>;
export const COMPLIANCE_STATUS_LABELS: Record<ComplianceStatus, string> = {
  valid: 'Valid',
  due_soon: 'Due soon',
  expired: 'Expired',
  missing: 'Missing',
  not_applicable: 'Not applicable',
};

export const MeterUtilitySchema = z.enum([
  'electricity',
  'gas',
  'water',
  'heating_oil',
  'solar_export',
]);
export type MeterUtility = z.infer<typeof MeterUtilitySchema>;
export const METER_UTILITY_LABELS: Record<MeterUtility, string> = {
  electricity: 'Electricity',
  gas: 'Gas',
  water: 'Water',
  heating_oil: 'Heating oil',
  solar_export: 'Solar export',
};
export const METER_DEFAULT_UNITS: Record<MeterUtility, string> = {
  electricity: 'kWh',
  gas: 'm³',
  water: 'm³',
  heating_oil: 'litres',
  solar_export: 'kWh',
};

export const PaymentFrequencySchema = z.enum(['monthly', 'quarterly', 'annually']);
export type PaymentFrequency = z.infer<typeof PaymentFrequencySchema>;

// Shared pieces --------------------------------------------------------------------

export const FileSummarySchema = z.object({
  id: z.uuid(),
  filename: z.string(),
  mime: z.string(),
  sizeBytes: z.number().int(),
  createdAt: Instant,
});
export type FileSummary = z.infer<typeof FileSummarySchema>;

export const PersonRefSchema = z.object({ id: z.uuid(), name: z.string() });
export type PersonRef = z.infer<typeof PersonRefSchema>;

export const SiteRefSchema = z.object({ id: z.uuid(), code: z.string(), name: z.string() });
export type SiteRef = z.infer<typeof SiteRefSchema>;

// Compliance -------------------------------------------------------------------------

export const ComplianceTypeSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  renewalIntervalMonths: z.number().int().nullable(),
  reminderDays: z.array(z.number().int()),
  siteKinds: z.array(z.string()),
  active: z.boolean(),
  sortOrder: z.number().int(),
});
export type ComplianceType = z.infer<typeof ComplianceTypeSchema>;

export const ComplianceTypeWriteSchema = z.object({
  name: z.string().trim().min(2).max(120),
  renewalIntervalMonths: z.number().int().min(1).max(240).nullable(),
  reminderDays: z.array(z.number().int().min(1).max(365)).max(6),
  siteKinds: z.array(z.string().min(1).max(40)).min(1),
  active: z.boolean(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
});
export type ComplianceTypeWrite = z.infer<typeof ComplianceTypeWriteSchema>;

export const ComplianceTypesResponseSchema = z.object({
  types: z.array(ComplianceTypeSchema),
  siteKinds: z.array(z.object({ code: z.string(), name: z.string() })),
});
export type ComplianceTypesResponse = z.infer<typeof ComplianceTypesResponseSchema>;

export const ComplianceCellSchema = z.object({
  typeId: z.uuid(),
  status: ComplianceStatusSchema,
  expiryDate: IsoDate.nullable(),
  recordId: z.uuid().nullable(),
  hasFile: z.boolean(),
  /** Days from today to expiry (negative when expired); null when there is none. */
  daysLeft: z.number().int().nullable(),
});
export type ComplianceCell = z.infer<typeof ComplianceCellSchema>;

export const ComplianceRowSchema = z.object({
  site: SiteRefSchema.extend({ kind: z.string() }),
  cells: z.array(ComplianceCellSchema),
});
export type ComplianceRow = z.infer<typeof ComplianceRowSchema>;

export const ComplianceMatrixResponseSchema = z.object({
  today: IsoDate,
  types: z.array(ComplianceTypeSchema),
  rows: z.array(ComplianceRowSchema),
  totals: z.record(ComplianceStatusSchema, z.number().int()),
  canManage: z.boolean(),
});
export type ComplianceMatrixResponse = z.infer<typeof ComplianceMatrixResponseSchema>;

export const ComplianceRecordSchema = z.object({
  id: z.uuid(),
  certificateDate: IsoDate.nullable(),
  expiryDate: IsoDate.nullable(),
  notes: z.string().nullable(),
  file: FileSummarySchema.nullable(),
  recordedBy: PersonRefSchema.nullable(),
  recordedAt: Instant,
  current: z.boolean(),
});
export type ComplianceRecord = z.infer<typeof ComplianceRecordSchema>;

export const ComplianceHistoryResponseSchema = z.object({
  site: SiteRefSchema,
  type: ComplianceTypeSchema,
  status: ComplianceStatusSchema,
  exempt: z.object({ reason: z.string().nullable() }).nullable(),
  records: z.array(ComplianceRecordSchema),
});
export type ComplianceHistoryResponse = z.infer<typeof ComplianceHistoryResponseSchema>;

export const ComplianceExemptionPutSchema = z.object({
  siteId: z.uuid(),
  typeId: z.uuid(),
  exempt: z.boolean(),
  reason: z.string().trim().max(255).optional(),
});

// Suppliers --------------------------------------------------------------------------

export const SupplierSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  trade: z.string().nullable(),
  contactName: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  address: z.string().nullable(),
  postcode: z.string().nullable(),
  notes: z.string().nullable(),
  active: z.boolean(),
  openJobs: z.number().int(),
});
export type Supplier = z.infer<typeof SupplierSchema>;

const optText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((v) => (v ? v : null));

/** For PATCH bodies: absent stays absent (unchanged), empty clears. */
const patchText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((v) => (v === undefined ? undefined : v ? v : null));

export const SupplierWriteSchema = z.object({
  name: z.string().trim().min(2).max(160),
  trade: optText(120),
  contactName: optText(120),
  phone: optText(40),
  email: z
    .union([z.email().max(160), z.literal(''), z.null()])
    .optional()
    .transform((v) => (v ? v : null)),
  address: optText(500),
  postcode: optText(16),
  notes: optText(2000),
  active: z.boolean().default(true),
});
export type SupplierWrite = z.input<typeof SupplierWriteSchema>;

export const SuppliersResponseSchema = z.object({ suppliers: z.array(SupplierSchema) });
export type SuppliersResponse = z.infer<typeof SuppliersResponseSchema>;

// Issues -----------------------------------------------------------------------------

export const IssueSchema = z.object({
  id: z.uuid(),
  site: SiteRefSchema,
  reportedBy: PersonRefSchema,
  category: PropertyCategorySchema,
  title: z.string(),
  description: z.string(),
  urgency: UrgencySchema,
  status: IssueStatusSchema,
  triageNote: z.string().nullable(),
  triagedBy: PersonRefSchema.nullable(),
  triagedAt: Instant.nullable(),
  workOrderId: z.uuid().nullable(),
  photos: z.array(FileSummarySchema),
  createdAt: Instant,
});
export type Issue = z.infer<typeof IssueSchema>;

export const IssuesResponseSchema = z.object({
  issues: z.array(IssueSchema),
  canTriage: z.boolean(),
});
export type IssuesResponse = z.infer<typeof IssuesResponseSchema>;

export const IssueTriageSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('triage'), note: z.string().trim().max(2000).optional() }),
  z.object({ action: z.literal('reject'), note: z.string().trim().min(3).max(2000) }),
]);
export type IssueTriage = z.infer<typeof IssueTriageSchema>;

// Work orders ------------------------------------------------------------------------

export const WorkOrderSummarySchema = z.object({
  id: z.uuid(),
  ref: z.string(),
  site: SiteRefSchema,
  title: z.string(),
  category: PropertyCategorySchema,
  priority: UrgencySchema,
  status: WorkOrderStatusSchema,
  supplier: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  /**
   * The estimate (the PO value). Only for viewers who may see job costs (property team,
   * leases.view holders); BMs/ABMs/RMs never receive the key (D61).
   */
  estimate: Money.nullable().optional(),
  poNumber: z.string().nullable(),
  legacyPo: z.string().nullable(),
  raisedBy: PersonRefSchema.nullable(),
  raisedAt: Instant,
  targetDate: IsoDate.nullable(),
  updatedAt: Instant,
});
export type WorkOrderSummary = z.infer<typeof WorkOrderSummarySchema>;

export const WorkOrderEventSchema = z.object({
  id: z.uuid(),
  at: Instant,
  actor: PersonRefSchema.nullable(),
  kind: z.string(),
  note: z.string().nullable(),
});
export type WorkOrderEvent = z.infer<typeof WorkOrderEventSchema>;

export const WorkOrderDetailSchema = WorkOrderSummarySchema.extend({
  description: z.string(),
  issue: z
    .object({
      id: z.uuid(),
      title: z.string(),
      reportedBy: PersonRefSchema,
      photos: z.array(FileSummarySchema),
    })
    .nullable(),
  approvedBy: PersonRefSchema.nullable(),
  approvedAt: Instant.nullable(),
  legacyApprovedByName: z.string().nullable(),
  /** The PO PDF (it shows the value): cost viewers only, like `estimate`. */
  poFile: FileSummarySchema.nullable().optional(),
  poSentAt: Instant.nullable(),
  poSentTo: z.string().nullable(),
  doneConfirmedBy: PersonRefSchema.nullable(),
  doneConfirmedAt: Instant.nullable(),
  doneNote: z.string().nullable(),
  /** Invoice amount, ref and file: cost viewers only (the keys are absent for BMs/RMs, D61). */
  invoiceAmount: Money.nullable().optional(),
  invoiceRef: z.string().nullable().optional(),
  invoiceFile: FileSummarySchema.nullable().optional(),
  /** Whether this viewer sees job costs (estimate/PO value, invoice, the PO PDF). */
  canViewCosts: z.boolean(),
  closedBy: PersonRefSchema.nullable(),
  closedAt: Instant.nullable(),
  cancelReason: z.string().nullable(),
  /**
   * Job files (supplier documents: quotes, imported job files): cost viewers only, always empty
   * for BMs/ABMs/RMs (D62). The branch's own issue photos are on `issue.photos`.
   */
  attachments: z.array(FileSummarySchema),
  events: z.array(WorkOrderEventSchema),
  /** What the viewer may do now (server-decided). */
  actions: z.array(WorkOrderActionSchema),
  /** Why approve is unavailable to this viewer, when it is a property-team member who raised it. */
  approveBlockedReason: z.string().nullable(),
});
export type WorkOrderDetail = z.infer<typeof WorkOrderDetailSchema>;

export const WorkOrdersResponseSchema = z.object({
  workOrders: z.array(WorkOrderSummarySchema),
  counts: z.record(WorkOrderStatusSchema, z.number().int()),
  canManage: z.boolean(),
  /** Whether estimates are included (per site for mixed scopes; false = none of them). */
  canViewCosts: z.boolean(),
});
export type WorkOrdersResponse = z.infer<typeof WorkOrdersResponseSchema>;

export const WorkOrderWriteSchema = z.object({
  siteId: z.uuid(),
  title: z.string().trim().min(3).max(200),
  description: z.string().trim().min(3).max(5000),
  category: PropertyCategorySchema,
  priority: UrgencySchema.default('normal'),
  supplierId: z.uuid().nullable().optional(),
  estimate: MoneyIn.nullable().optional(),
  targetDate: IsoDate.nullable().optional(),
});
export type WorkOrderWrite = z.input<typeof WorkOrderWriteSchema>;

export const WorkOrderPatchSchema = WorkOrderWriteSchema.omit({ siteId: true }).partial();
export type WorkOrderPatch = z.input<typeof WorkOrderPatchSchema>;

export const IssueConvertSchema = WorkOrderWriteSchema.omit({ siteId: true });
export type IssueConvert = z.input<typeof IssueConvertSchema>;

export const WorkOrderNoteSchema = z.object({ note: z.string().trim().min(1).max(2000) });
export const WorkOrderDoneSchema = z.object({ note: z.string().trim().max(2000).optional() });
export const WorkOrderCancelSchema = z.object({ reason: z.string().trim().min(3).max(2000) });

export const WorkOrderActionResponseSchema = z.object({
  workOrder: WorkOrderDetailSchema,
  /** Set when the PO email could not go (no supplier email, provider error, allowlist). */
  poWarning: z.string().nullable().optional(),
});
export type WorkOrderActionResponse = z.infer<typeof WorkOrderActionResponseSchema>;

// Sites, overview --------------------------------------------------------------------

export const ComplianceCountsSchema = z.object({
  valid: z.number().int(),
  due_soon: z.number().int(),
  expired: z.number().int(),
  missing: z.number().int(),
  not_applicable: z.number().int(),
});
export type ComplianceCounts = z.infer<typeof ComplianceCountsSchema>;

export const PropertySiteSummarySchema = SiteRefSchema.extend({
  kind: z.string(),
  regionName: z.string().nullable(),
  compliance: ComplianceCountsSchema,
  openJobs: z.number().int(),
  openIssues: z.number().int(),
  metersDue: z.number().int(),
});
export type PropertySiteSummary = z.infer<typeof PropertySiteSummarySchema>;

export const LeaseSchema = z.object({
  id: z.uuid(),
  site: SiteRefSchema,
  landlord: z.string().nullable(),
  annualRent: Money.nullable(),
  paymentFrequency: PaymentFrequencySchema.nullable(),
  startDate: IsoDate.nullable(),
  endDate: IsoDate.nullable(),
  termYears: z.string().nullable(),
  noticeMonths: z.number().int().nullable(),
  noticeText: z.string().nullable(),
  /** Last day to serve notice before the next break or the end (derived). */
  noticeDeadline: IsoDate.nullable(),
  breakDates: z.array(IsoDate),
  reviewDates: z.array(IsoDate),
  renewalNote: z.string().nullable(),
  notes: z.string().nullable(),
  updatedAt: Instant,
});
export type Lease = z.infer<typeof LeaseSchema>;

export const LeasePatchSchema = z.object({
  landlord: patchText(255),
  annualRent: MoneyIn.nullable().optional(),
  paymentFrequency: PaymentFrequencySchema.nullable().optional(),
  startDate: IsoDate.nullable().optional(),
  endDate: IsoDate.nullable().optional(),
  noticeMonths: z.number().int().min(0).max(60).nullable().optional(),
  breakDates: z.array(IsoDate).max(10).optional(),
  reviewDates: z.array(IsoDate).max(10).optional(),
  renewalNote: patchText(2000),
  notes: patchText(4000),
});
export type LeasePatch = z.input<typeof LeasePatchSchema>;

export const LeasesResponseSchema = z.object({ leases: z.array(LeaseSchema) });
export type LeasesResponse = z.infer<typeof LeasesResponseSchema>;

export const ContractSchema = z.object({
  id: z.uuid(),
  service: z.string(),
  supplier: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  startDate: IsoDate.nullable(),
  renewalDate: IsoDate.nullable(),
  endDate: IsoDate.nullable(),
  accountRef: z.string().nullable(),
  notes: z.string().nullable(),
});
export type Contract = z.infer<typeof ContractSchema>;

export const MeterSchema = z.object({
  id: z.uuid(),
  site: SiteRefSchema,
  utility: MeterUtilitySchema,
  serial: z.string().nullable(),
  label: z.string().nullable(),
  unit: z.string(),
  active: z.boolean(),
  lastReading: z
    .object({ period: YearMonthPeriodSchema, reading: z.string(), readOn: IsoDate })
    .nullable(),
  /** No reading for the current month yet. */
  due: z.boolean(),
});
export type Meter = z.infer<typeof MeterSchema>;

export const PropertySiteDetailSchema = z.object({
  site: PropertySiteSummarySchema.extend({
    address: z.string().nullable(),
    postcode: z.string().nullable(),
  }),
  compliance: z.array(
    z.object({
      type: ComplianceTypeSchema,
      cell: ComplianceCellSchema,
      fileId: z.uuid().nullable(),
    }),
  ),
  workOrders: z.array(WorkOrderSummarySchema),
  issues: z.array(IssueSchema),
  meters: z.array(MeterSchema),
  contracts: z.array(ContractSchema),
  /** Present only for leases.view holders (D60); BMs/RMs never receive it. */
  lease: LeaseSchema.nullable().optional(),
  can: z.object({
    reportIssue: z.boolean(),
    recordMeter: z.boolean(),
    manageJobs: z.boolean(),
    manageCompliance: z.boolean(),
    viewLease: z.boolean(),
    /** Job costs (estimates) are included for this site. */
    viewCosts: z.boolean(),
  }),
});
export type PropertySiteDetail = z.infer<typeof PropertySiteDetailSchema>;

export const PropertyOverviewSchema = z.object({
  today: IsoDate,
  period: YearMonthPeriodSchema,
  sites: z.array(PropertySiteSummarySchema),
  team: z
    .object({
      issuesToTriage: z.number().int(),
      awaitingApproval: z.number().int(),
      awaitingMyApproval: z.number().int(),
      doneAwaitingClose: z.number().int(),
      certificatesExpired: z.number().int(),
      certificatesDueSoon: z.number().int(),
      certificatesMissing: z.number().int(),
    })
    .nullable(),
  can: z.object({
    reportIssue: z.boolean(),
    manageJobs: z.boolean(),
    manageCompliance: z.boolean(),
    manageSuppliers: z.boolean(),
    viewLeases: z.boolean(),
    recordMeters: z.boolean(),
  }),
});
export type PropertyOverview = z.infer<typeof PropertyOverviewSchema>;

// Meters ----------------------------------------------------------------------------

export const MetersResponseSchema = z.object({
  period: YearMonthPeriodSchema,
  meters: z.array(MeterSchema),
});
export type MetersResponse = z.infer<typeof MetersResponseSchema>;

export const MeterWriteSchema = z.object({
  siteId: z.uuid(),
  utility: MeterUtilitySchema,
  serial: optText(64),
  label: optText(120),
  unit: z.string().trim().min(1).max(16).optional(),
  active: z.boolean().default(true),
});
export type MeterWrite = z.input<typeof MeterWriteSchema>;

export const MeterReadingSchema = z.object({
  id: z.uuid(),
  period: YearMonthPeriodSchema,
  reading: z.string(),
  readOn: IsoDate,
  note: z.string().nullable(),
  photo: FileSummarySchema.nullable(),
  recordedBy: PersonRefSchema.nullable(),
  recordedAt: Instant,
  /** Change since the previous reading (null for the first). */
  usage: z.string().nullable(),
});
export type MeterReading = z.infer<typeof MeterReadingSchema>;

export const MeterReadingsResponseSchema = z.object({
  meter: MeterSchema,
  readings: z.array(MeterReadingSchema),
});
export type MeterReadingsResponse = z.infer<typeof MeterReadingsResponseSchema>;

// Settings --------------------------------------------------------------------------

export const PropertySettingsSchema = z.object({
  escalationPerson: PersonRefSchema.nullable(),
  /** Who overdue certificates escalate to today, and why. */
  escalationResolved: z.object({
    people: z.array(PersonRefSchema),
    via: z.enum(['setting', 'ops_director_seat', 'director_role', 'none']),
  }),
  poPrefix: z.string(),
  poCompanyName: z.string(),
  poCompanyAddress: z.string().nullable(),
  poInvoiceEmail: z.string().nullable(),
  poTerms: z.string().nullable(),
  nextPoNumber: z.number().int(),
});
export type PropertySettings = z.infer<typeof PropertySettingsSchema>;

export const PropertySettingsPatchSchema = z.object({
  escalationPersonId: z.uuid().nullable().optional(),
  poPrefix: z
    .string()
    .trim()
    .regex(/^[A-Z0-9]{1,8}$/, 'Capital letters and digits, up to 8')
    .optional(),
  poCompanyName: z.string().trim().min(2).max(160).optional(),
  poCompanyAddress: patchText(500),
  poInvoiceEmail: z
    .union([z.email().max(160), z.literal(''), z.null()])
    .optional()
    .transform((v) => (v === undefined ? undefined : v ? v : null)),
  poTerms: patchText(2000),
});
export type PropertySettingsPatch = z.input<typeof PropertySettingsPatchSchema>;

export const PropertyPeopleSearchResponseSchema = z.object({ people: z.array(PersonRefSchema) });

export const PropertyIdResponseSchema = z.object({ id: z.uuid() });
export const PropertyOkResponseSchema = z.object({ ok: z.literal(true) });
